/* eslint-disable class-methods-use-this */
import { makeAutoObservable } from 'mobx';
import apolloClient from 'api/apolloClient';
import {
  ADD_PORTFOLIO_TO_CART,
  CREATE_CART,
  COPY_CART,
  UPDATE_BC,
  REMOVE_BC_BY_INDEX,
  SAVE_BROKER_CART,
  DELETE_CART,
  UPDATE_CART_NAME,
  SAVE_SHORT_NAME_TO_BC,
} from 'api/queries/cartServiceV2';
import { Cart, CartSource, PortfolioConfigRequest } from 'types/cartServiceV2';
import { FetchResult } from '@apollo/client';
import { CartBrowsingCriteria, RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';

export const INITIAL_BROWSING_CRITERIA = {
  effectiveDate: '',
  zipCode: '',
  state: '',
  county: '',
  shortName: {},
};

export const INITIAL_CART_OBJECT: Cart = Object.freeze({
  id: '',
  groupId: '',
  cartName: '',
  premiumCostAnswers: {},
  creationDateTime: '',
  cartItems: [],
  lastUpdateTime: '',
  rateSegment: 'GROUP',
  lastUpdateBrowsingCriteria: { ...INITIAL_BROWSING_CRITERIA },
});

/**
 * @DEPRECATED If you don't use store, move api calls into api/operations folder.
 */
export class CartStoreV2 {
  async createCart(
    groupId: string,
    cartSource: CartSource,
    browsingCriteria: CartBrowsingCriteria,
    name?: string,
    rateSegment?: RateSegment,
  ): Promise<FetchResult<{ createCart: Cart }>> {
    return apolloClient.mutate<{ createCart: Cart }>({
      mutation: CREATE_CART,
      fetchPolicy: 'no-cache',
      variables: { groupId, cartSource, browsingCriteria, name, rateSegment },
    });
  }

  async addPortfolioToCart(
    portfolioConfigRequest: PortfolioConfigRequest,
  ): Promise<FetchResult<{ addPortfolioConfigToCart: Cart }>> {
    return apolloClient.mutate<{ addPortfolioConfigToCart: Cart }>({
      mutation: ADD_PORTFOLIO_TO_CART,
      fetchPolicy: 'no-cache',
      variables: { portfolioConfigRequest },
    });
  }

  async saveShortNameToBC(
    sponsorId: string,
    bc: CartBrowsingCriteria,
  ): Promise<FetchResult<{ saveShortNameToBC: CartBrowsingCriteria }>> {
    return apolloClient.mutate<{ saveShortNameToBC: CartBrowsingCriteria }>({
      mutation: SAVE_SHORT_NAME_TO_BC,
      fetchPolicy: 'no-cache',
      variables: { sponsorId, bc },
    });
  }

  async removeBcByIndex(
    cartId: string,
    portfolioId: string,
    index: number,
  ): Promise<FetchResult<{ removeBCByIndex: Cart }>> {
    return apolloClient.mutate<{ removeBCByIndex: Cart }>({
      mutation: REMOVE_BC_BY_INDEX,
      fetchPolicy: 'no-cache',
      variables: { cartId, portfolioId, index },
    });
  }

  async updateBC(cartId: string, browsingCriteria: CartBrowsingCriteria): Promise<FetchResult<{ updateLastBC: Cart }>> {
    return apolloClient.mutate<{ updateLastBC: Cart }>({
      mutation: UPDATE_BC,
      fetchPolicy: 'no-cache',
      variables: { cartId, browsingCriteria },
    });
  }

  async copyCart(cartId: string, cartName = ''): Promise<FetchResult<{ copyCart: Cart }>> {
    return apolloClient.mutate<{ copyCart: Cart }>({
      mutation: COPY_CART,
      variables: { id: cartId, name: cartName },
    });
  }

  // Save template
  async saveBrokerCart(
    cartId: string,
    name: string,
    rateSegment: RateSegment,
  ): Promise<FetchResult<{ saveBrokerCart: Cart }>> {
    return apolloClient.mutate<{ saveBrokerCart: Cart }>({
      mutation: SAVE_BROKER_CART,
      variables: { cartId, name, rateSegment },
    });
  }

  async deleteCart(id: string): Promise<FetchResult<{ deleteCart: boolean }>> {
    return apolloClient.mutate<{ deleteCart: boolean }>({
      mutation: DELETE_CART,
      variables: { id },
    });
  }

  async updateCartName(id: string, name: string): Promise<FetchResult<{ updateCartName: Cart }>> {
    return apolloClient.mutate<{ updateCartName: Cart }>({
      mutation: UPDATE_CART_NAME,
      variables: { id, name },
    });
  }

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
